import type { SVGProps } from 'react';

/** Inline stroke icons (11 §1.2: SVG only, no icon font, no CDN). Drawn for this project on a 24-unit grid.
 *  Directional icons (arrows, chevrons) flip in RTL via `rtl:-scale-x-100` (11 §2.2). */
const PATHS = {
  dashboard: 'M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z',
  arrivals: 'M14 4h5v16h-5M3 12h11M10 8l4 4-4 4',
  departures: 'M10 4H5v16h5M9 12h11M16 8l4 4-4 4',
  inHouse: 'M3 18v-7h18v7M3 14h18M6 11V7h5v4M3 18v2M21 18v2',
  reservations: 'M4 6h16v14H4zM4 10h16M8 3v5M16 3v5M8 14h3M8 17h6',
  tape: 'M3 5h18v14H3zM3 10h18M3 14.5h18M8 5v14M7 7.5h6M11 12h7M5 16.8h5',
  groups:
    'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3 19c0-3 2.7-5 6-5s6 2 6 5M16 5.2a3 3 0 0 1 0 5.6M18 14.4c1.8.6 3 2.4 3 4.6',
  channels:
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c2.5 2.5 3.5 5.5 3.5 9s-1 6.5-3.5 9c-2.5-2.5-3.5-5.5-3.5-9s1-6.5 3.5-9z',
  guests: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5',
  cashier: 'M3 7h18v12H3zM3 11h18M7 15h3M16 4H6',
  ar: 'M4 21V5l8-3 8 3v16M4 21h16M9 9h2M13 9h2M9 13h2M13 13h2M10 21v-4h4v4',
  housekeeping: 'M12 3v8M8 11h8l1 10H7zM10 15v6M14 15v6',
  maintenance:
    'M14.5 6.5a4 4 0 0 0 5 5L21 13l-8 8-3-3 1.5-1.5a4 4 0 0 1-5-5L3 8l2-2 3.5 3.5L11 7 7.5 3.5 9.5 1.5z',
  handover: 'M8 4h8v3H8zM6 5.5H4V21h16V5.5h-2M8 12h8M8 16h5',
  nightAudit: 'M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5zM15 4v3M13.5 5.5h3',
  reports: 'M4 20h16M6 20v-6M10 20V8M14 20v-9M18 20V5',
  forecast: 'M4 20h16M4 16l5-5 4 3 7-8M15 6h5v5',
  auditLog: 'M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6zM8.5 12l2.5 2.5 4.5-5',
  health: 'M3 12h4l2-5 4 10 2-5h6',
  license:
    'M8.5 15.5a4.5 4.5 0 1 1 3.9-6.7H21v3h-2v3h-3v-3h-3.6a4.5 4.5 0 0 1-3.9 3.7zM7.5 11.5h.01',
  security: 'M6 11h12v10H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3M12 15v2',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM16 16l5 5',
  plus: 'M12 5v14M5 12h14',
  walkIn:
    'M13 4.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM9 21l2-7 3 3v5M7 12l3-4 3 1 2 3 3 1M11 14l-1-4',
  chevron: 'M9 6l6 6-6 6',
  sidebar: 'M3 4h18v16H3zM15 4v16',
  keyboard: 'M3 6h18v12H3zM7 10h.01M11 10h.01M15 10h.01M7 14h10',
  language:
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3.5 9h17M3.5 15h17M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18',
  theme: 'M12 21a9 9 0 0 0 0-18zM12 3a9 9 0 0 0 0 18',
  logout: 'M10 4H5v16h5M14 8l4 4-4 4M18 12H9',
  lock: 'M6 11h12v10H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  printer: 'M7 9V3h10v6M7 17H4v-8h16v8h-3M7 14h10v7H7z',
  download: 'M12 4v11M7 10l5 5 5-5M5 20h14',
  camera: 'M4 8h4l2-3h4l2 3h4v11H4zM12 17a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  check: 'M5 12.5l4.5 4.5L19 7',
  close: 'M6 6l12 12M18 6L6 18',
  alert: 'M12 3l10 18H2zM12 10v5M12 18h.01',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v6M12 7.5h.01',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5',
  building: 'M5 21V4h14v17M3 21h18M9 8h2M13 8h2M9 12h2M13 12h2M10 21v-4h4v4',
  ticket: 'M3 8V5h18v3a2.5 2.5 0 0 0 0 5v3H3v-3a2.5 2.5 0 0 0 0-5zM14 5v11',
  rocket:
    'M12 15l-3-3c1-4 4-8 10-9-1 6-5 9-9 10zM9 12l-4 1-2-2 5-3M12 15l-1 4 2 2 3-5M14.5 9.5h.01',
  flag: 'M5 21V4M5 4h11l-2 4 2 4H5',
  layers: 'M12 3l9 5-9 5-9-5zM3 12.5l9 5 9-5M3 16.5l9 5 9-5',
  sliders: 'M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M14 4v4M8 10v4M16 16v4',
} as const;

export type IconName = keyof typeof PATHS;

const DIRECTIONAL: ReadonlySet<IconName> = new Set<IconName>([
  'chevron',
  'arrivals',
  'departures',
  'logout',
  'forecast',
]);

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 18, className = '', ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 ${DIRECTIONAL.has(name) ? 'rtl:-scale-x-100' : ''} ${className}`}
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
